import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Avatar, 
  TextField, 
  Button, 
  Grid, 
  Divider, 
  Alert,
  IconButton,
  InputAdornment,
  Paper,
  Container,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { 
  Edit, 
  Save, 
  Cancel, 
  Person, 
  Email, 
  Phone, 
  LocationOn, 
  AccountCircle,
  Visibility,
  VisibilityOff,
  Lock,
  Security,
  CheckCircle
} from '@mui/icons-material';
import { api } from '../api/client';
import { useNavigate } from 'react-router-dom';

interface UserProfile {
  id?: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  country: string;
  city: string;
  accountNumber?: string;
  userType?: string;
  avatar?: string;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile>({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    country: '',
    city: '',
    accountNumber: '',
    userType: '',
    avatar: ''
  });
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadUserProfile();
  }, []);

  const handleChange = (field: keyof UserProfile, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!profile.username || !profile.firstName || !profile.lastName || !profile.email) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    if (password && password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const updateData = {
        username: profile.username,
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        mobileNumber: profile.mobileNumber,
        country: profile.country,
        city: profile.city,
        accountNumber: profile.accountNumber,
        userType: profile.userType,
        ...(password && { password }),
        ...(confirmPassword && { confirmPassword })
      };

      const response = await api.put(`/api/users/profile`, updateData);
      
      if (response.status === 200) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditing(false);
        setPassword('');
        setConfirmPassword('');
        
        const userInfo = localStorage.getItem('user_info');
        if (userInfo) {
          const userData = JSON.parse(userInfo);
          const updatedUserData = { ...userData, ...updateData };
          localStorage.setItem('user_info', JSON.stringify(updatedUserData));
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update profile';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const loadUserProfile = () => {
    try {
      const userInfo = localStorage.getItem('user_info');
      const token = localStorage.getItem('access_token');
      const parse = (str: string | null) => {
        try { return str ? JSON.parse(str) : {}; } catch { return {}; }
      };
      const userData = parse(userInfo);
      let tokenClaims: any = {};
      if (token && token.split('.').length === 3) {
        try {
          const payload = token.split('.')[1];
          tokenClaims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))); 
        } catch {}
      }

      setProfile({
        id: userData.id,
        username: userData.username || userData.preferred_username || tokenClaims.preferred_username || '',
        firstName: userData.firstName || userData.given_name || userData.first_name || tokenClaims.given_name || '',
        lastName: userData.lastName || userData.family_name || userData.last_name || tokenClaims.family_name || '',
        email: userData.email || tokenClaims.email || '',
        mobileNumber: userData.mobileNumber || userData.phone_number || tokenClaims.phone_number || '',
        country: userData.country || tokenClaims.country || '',
        city: userData.city || tokenClaims.city || '',
        accountNumber: userData.accountNumber || tokenClaims.accountNumber || '',
        userType: userData.userType || userData.user_type || '',
        avatar: userData.avatar || ''
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
      setMessage({ type: 'error', text: 'Failed to load user profile' });
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setCurrentPassword('');
    setPassword('');
    setConfirmPassword('');
    setMessage(null);
    
    // Restore original profile data
    loadUserProfile();
  };

  const handleShowPasswordForm = () => {
    setShowPasswordForm(true);
    setPassword('');
    setConfirmPassword('');
    setCurrentPassword('');
    setMessage(null);
  };

  const handleCancelPasswordForm = () => {
    setShowPasswordForm(false);
    setPassword('');
    setConfirmPassword('');
    setCurrentPassword('');
    setMessage(null);
  };

  const handleUpdatePassword = async () => {

    if (!currentPassword || !password || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill current, new and confirm password' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'New password and confirmation do not match' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (password.length < 8) {
      setMessage({ type: 'error', text: 'New password must be at least 8 characters' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    try {
      setSaving(true);
      setMessage({ type: 'success', text: 'Updating password...' });
      await api.post('/api/auth/change-password', { currentPassword, newPassword: password });
      setMessage({ type: 'success', text: 'Password updated successfully' });
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Failed to change password';
      setMessage({ type: 'error', text: msg });
      console.error('[PROFILE] Change password failed:', e);
    } finally {
      setSaving(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getInitials = () => {
    const first = profile.firstName ? profile.firstName[0] : '';
    const last = profile.lastName ? profile.lastName[0] : '';
    return (first + last).toUpperCase() || profile.username[0]?.toUpperCase() || 'U';
  };

  const getFullName = () => {
    if (profile.firstName && profile.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    return profile.username || 'User';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper 
          elevation={24} 
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              User Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
              Manage your account information and preferences
            </Typography>
          </Box>

          {message && (
            <Alert 
              severity={message.type} 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { fontSize: '1.5rem' }
              }}
            >
              {message.text}
            </Alert>
          )}

          <Card sx={{ mb: 4, background: 'rgba(30, 60, 114, 0.05)' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  mb: 3,
                  fontSize: '2rem',
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)'
                }} 
                src={profile.avatar}
              >
                {getInitials()}
              </Avatar>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                {getFullName()}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                {profile.email}
              </Typography>
              {profile.userType && (
                <Typography variant="body2" color="text.secondary">
                  Account Type: {profile.userType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Typography>
              )}
              {profile.accountNumber ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    Account Verified: {profile.accountNumber}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<Security />}
                    onClick={() => navigate('/account-verification')}
                    sx={{
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Verify Your Account
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Add your SMEDCO account details to get full access
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                  Personal Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  value={profile.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  value={profile.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  value={profile.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Mobile Number"
                  value={profile.mobileNumber}
                  onChange={(e) => handleChange('mobileNumber', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              {profile.accountNumber && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Account Number"
                    value={profile.accountNumber}
                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                    fullWidth
                    disabled={!editing}
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AccountCircle sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                  Location Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country"
                  value={profile.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={profile.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  fullWidth
                  disabled={!editing}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />
              </Grid>


              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {editing ? (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                        sx={{
                          py: 1.5,
                          px: 4,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                          boxShadow: '0 8px 25px rgba(30, 60, 114, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #152a5a 0%, #1e3c72 100%)',
                            boxShadow: '0 12px 35px rgba(30, 60, 114, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #8fa3c7 0%, #9bb3d1 100%)',
                            transform: 'none',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        startIcon={<Cancel />}
                        sx={{
                          py: 1.5,
                          px: 4,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'primary.50',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => setEditing(true)}
                      startIcon={<Edit />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          backgroundColor: 'primary.50',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Edit Profile
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>

          {/* Separate Password Update Section */}
          <Card sx={{ mt: 4, background: 'rgba(30, 60, 114, 0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Change Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update your account password. This is separate from your profile information.
                  </Typography>
                </Box>
                {!showPasswordForm && (
                  <Button
                    variant="outlined"
                    onClick={handleShowPasswordForm}
                    startIcon={<Lock />}
                    sx={{
                      py: 1,
                      px: 3,
                      borderRadius: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'primary.50',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Update Password
                  </Button>
                )}
              </Box>
              
              {showPasswordForm && (
                <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Current Password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: 'primary.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            edge="end"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="New Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{ color: 'text.secondary' }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancelPasswordForm}
                      startIcon={<Cancel />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: 'text.secondary',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'text.primary',
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleUpdatePassword}
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <Lock />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                        boxShadow: '0 8px 25px rgba(211, 47, 47, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
                          boxShadow: '0 12px 35px rgba(211, 47, 47, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #ffcdd2 0%, #ffcdd2 100%)',
                          transform: 'none',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {saving ? 'Updating...' : 'Update Password'}
                    </Button>
                  </Box>
                </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              © 2024 SMEDCO. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Profile; 
